import React, { useState, useEffect, useRef } from 'react';
import { TextArea, Dropdown, Modal } from 'tea-component';
import { PromptType } from '@op/components/GPT/types';
import { getPromptText } from '@op/components/GPT/utils';
import SvgIcon from '@home/components/SvgIcon';
import TextBubbles from '@home/components/TextBubbles';
import './index.scss';
import CommonBubble from '@home/components/CommonBubble';
import useLanguage from '@op/hooks/useLanguage';
import SavePromptModal from './SavePromptModal';
import DOMPurify from 'dompurify';

export interface IPromptSectionProps {
  prompt: string;
  setPrompt: (value: string) => void;
  gptOptions?: string;
  errorText: string;
  setErrorText: (value: string) => void;
  realPromptList: PromptType[];
  defaultPromptList: any[];
  userPromptList: any[];
  togglePromptModal: (modalType?: string) => void;
  onSavePrompt?: (promptName: string, promptText: string) => Promise<any>;
  onUpdatePrompt?: (
    id: string,
    promptName: string,
    promptText: string,
  ) => Promise<any>;
  onDeletePrompt?: (id: string) => Promise<any>;
  onApplyPrompt?: (id: string) => Promise<any>;
  isModal?: boolean;
  activePromptId: string;
  setActivePromptId: (value: string) => void;
  promptEdit: React.MutableRefObject<boolean>;
  fullClick?: () => void;
  textareaHeight?: number;
}

const PromptSection: React.FC<IPromptSectionProps> = ({
  prompt,
  setPrompt,
  gptOptions = 'basic',
  errorText: _errorText,
  setErrorText,
  realPromptList,
  defaultPromptList: _defaultPromptList,
  userPromptList,
  togglePromptModal,
  onSavePrompt,
  onUpdatePrompt,
  onDeletePrompt,
  onApplyPrompt,
  isModal = false,
  activePromptId,
  setActivePromptId,
  promptEdit,
  fullClick,
  textareaHeight,
}) => {
  const { isEn, formatMessage } = useLanguage();
  const isBasic = gptOptions === 'basic';
  const currentRealPromptList = realPromptList || [];

  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [saveModalMode, setSaveModalMode] = useState<'save' | 'edit'>('save');
  const [editingPromptId, setEditingPromptId] = useState<string>('');
  const [modalPromptName, setModalPromptName] = useState('');
  const [modalPromptContent, setModalPromptContent] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // When language changes, if a default prompt is active, update prompt text to new language
  useEffect(() => {
    if (!activePromptId) return;
    const matched = currentRealPromptList.find(
      (item) => item?.name?.['en-US'] === activePromptId,
    );
    if (matched) {
      setPrompt(getPromptText(matched));
    }
  }, [isEn]);

  const openSaveModal = () => {
    setSaveModalMode('save');
    setEditingPromptId('');
    setModalPromptName('');
    setModalPromptContent(prompt);
    setSaveModalVisible(true);
  };

  const openEditModal = (item: any) => {
    setSaveModalMode('edit');
    setEditingPromptId(item.id || '');
    setModalPromptName(item.prompt_name || '');
    setModalPromptContent(item.prompt_detail || '');
    setSaveModalVisible(true);
  };

  const closeSaveModal = () => {
    setSaveModalVisible(false);
    setEditingPromptId('');
    setModalPromptName('');
    setModalPromptContent('');
  };

  const handleDeletePrompt = (item: any) => {
    const itemId = item.id;
    if (!itemId || !onDeletePrompt) return;
    const confirmed = Modal.confirm({
      message: formatMessage({ id: 'Delete Prompt' }),
      description: formatMessage({
        id: 'Are you sure you want to delete this prompt?',
      }),
      okText: formatMessage({ id: 'Delete' }),
      cancelText: formatMessage({ id: 'Cancel' }),
      maskStyle: {
        zIndex: 1001,
      },
    });
    confirmed.then((yes: boolean) => {
      if (yes) {
        onDeletePrompt(itemId).catch((error) => {
          console.log(error);
        });
      }
    });
  };

  const handleApplyPrompt = (title: string, onConfirm: () => void) => {
    if (!prompt.trim()) {
      onConfirm();
      return;
    }
    const confirmed = Modal.confirm({
      icon: 'error',
      message: formatMessage({ id: 'Replace current prompt?' }),
      description: formatMessage(
        { id: 'Replacing it with "{title}" will lose the current text.' },
        { title },
      ),
      okText: formatMessage({ id: 'Replace' }),
      cancelText: formatMessage({ id: 'Cancel' }),
    });
    confirmed.then((yes: boolean) => {
      if (yes) onConfirm();
    });
  };

  return (
    <div
      className={`dbo-prompt-container${
        isFocused ? ' dbo-prompt-container-active' : ''
      }`}
    >
      {/* Prompt toolbar */}
      <div className="dbo-prompt-container-toolbar">
        <div className="dbo-prompt-container-toolbar-left">
          <div className="item-label">
            {formatMessage({ id: 'Prompt' })}
            {isBasic ? (
              <span style={{ color: '#ef5858', marginLeft: 5 }}>*</span>
            ) : (
              <span>({formatMessage({ id: 'Optional' })})</span>
            )}
          </div>
        </div>
        <div className="dbo-prompt-container-toolbar-right">
          {currentRealPromptList?.length > 0 && (
            <div className="dbo-prompt-pill-wrapper">
              <span className="dbo-prompt-container-toolbar-label">
                {formatMessage({ id: 'Quick-1' })}
              </span>
              {currentRealPromptList?.map((item: PromptType, index: number) => {
                const promptId = item?.name?.['en-US'] || '';
                const isActive =
                  !!activePromptId && activePromptId === promptId;
                const promptNumber = index + 1;
                return (
                  <div
                    key={item.value}
                    className={`dbo-prompt-pill ${
                      isActive ? 'dbo-prompt-pill--active' : ''
                    }`}
                    onClick={() => {
                      promptEdit.current = true;
                      if (activePromptId === promptId) {
                        setActivePromptId('');
                        setPrompt('');
                        if (isBasic) {
                          setErrorText(
                            formatMessage({ id: 'Please enter prompt.' }),
                          );
                        }
                      } else {
                        setPrompt(getPromptText(item));
                        setActivePromptId(promptId);
                        setErrorText('');
                      }
                    }}
                  >
                    {!isModal && (
                      // <SvgIcon type={'chevron-bottom-right'} className="dbo-prompt-pill-arrow" />
                      <i className="icon-chevron-bottom-right"></i>
                    )}
                    <CommonBubble
                      content={
                        <div className="dbo-scroll-bubble-scroll">
                          <span
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                isEn
                                  ? item['en-US']
                                  : (item['zh-CN'] as string),
                              ),
                            }}
                            className="dbo-scroll-bubble-scroll-content"
                          ></span>
                        </div>
                      }
                      overlayClassName="dbo-scroll-bubble"
                    >
                      {isModal
                        ? promptNumber
                        : item?.name?.[isEn ? 'en-US' : 'zh-CN'] ||
                          promptNumber}
                    </CommonBubble>
                  </div>
                );
              })}
            </div>
          )}
          <div className="dbo-prompt-container-toolbar-btn">
            <Dropdown
              className="dbo-prompt-saved-dropdown"
              appearance="button"
              placement="bottom-end"
              trigger="click"
              button={
                <div className="dbo-prompt-dropdown-btn">
                  <i className="dbo-icon-bookmark-start --active"></i>
                  {formatMessage({ id: 'Saved Prompts' })}
                </div>
              }
            >
              {() => (
                <div className="dbo-prompt-saved-panel">
                  <div className="dbo-prompt-saved-header">
                    <i className="dbo-icon-bookmark-start dbo-prompt-saved-header-icon" />
                    <span>{formatMessage({ id: 'Prompts' })}</span>
                  </div>
                  <div className="dbo-prompt-saved-list">
                    {/* Default prompts */}
                    {currentRealPromptList.map((item: any, index: number) => {
                      const detail = getPromptText(item);
                      const promptId = item.name?.['en-US'] || '';
                      return (
                        <div
                          key={item.id || `default-${index}`}
                          className={`dbo-prompt-saved-item ${
                            activePromptId === promptId
                              ? 'dbo-prompt-saved-item--selected'
                              : ''
                          }`}
                          onClick={() => {
                            const promptTitle =
                              item.name?.[isEn ? 'en-US' : 'zh-CN'] || '';
                            handleApplyPrompt(promptTitle, () => {
                              setPrompt(detail);
                              setActivePromptId(promptId);
                              setErrorText('');
                              promptEdit.current = true;
                            });
                          }}
                        >
                          <div className="dbo-prompt-saved-item-name-row">
                            <span className="dbo-prompt-saved-item-name">
                              {item.name?.[isEn ? 'en-US' : 'zh-CN'] ||
                                formatMessage(
                                  { id: 'Default prompt {index}' },
                                  { index },
                                )}
                            </span>
                            <span className="dbo-prompt-saved-item-badge">
                              {formatMessage({ id: 'Default' })}
                            </span>
                          </div>
                          <TextBubbles
                            placement="left-center"
                            rowNum={1}
                            text={detail}
                            tipContent={
                              <div className="dbo-scroll-bubble-scroll">
                                {detail}
                              </div>
                            }
                            className={'dbo-prompt-saved-item-detail'}
                            bubbleProps={{
                              overlayClassName: 'dbo-scroll-bubble',
                            }}
                          />
                        </div>
                      );
                    })}
                    {/* User saved prompts */}
                    {userPromptList.map((item: any, index: number) => (
                      <div
                        key={item.id || item.update_time || `user-${index}`}
                        className={`dbo-prompt-saved-item ${
                          activePromptId === (item.prompt_name || '')
                            ? 'dbo-prompt-saved-item--selected'
                            : ''
                        }`}
                        onClick={() => {
                          const promptTitle = item.prompt_name || '';
                          handleApplyPrompt(promptTitle, () => {
                            setPrompt(item.prompt_detail);
                            setActivePromptId(item.prompt_name || '');
                            setErrorText('');
                            promptEdit.current = true;
                            if (item.id && onApplyPrompt) {
                              onApplyPrompt(item.id).catch((error) => {
                                console.log(error);
                              });
                            }
                          });
                        }}
                      >
                        <div className="dbo-prompt-saved-item-name-row">
                          <span className="dbo-prompt-saved-item-name">
                            {item.prompt_name ||
                              formatMessage({ id: 'Custom' })}
                          </span>
                          <div className="dbo-prompt-saved-item-actions">
                            <div className="dbo-prompt-saved-item-actions-item">
                              <SvgIcon
                                type="pencil"
                                className="dbo-prompt-saved-item-action-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditModal(item);
                                }}
                              />
                            </div>
                            <div className="dbo-prompt-saved-item-actions-item">
                              <SvgIcon
                                type="trash-can-icon"
                                className="dbo-prompt-saved-item-action-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePrompt(item);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <TextBubbles
                          rowNum={1}
                          text={item.prompt_detail}
                          tipContent={
                            <div className="dbo-scroll-bubble-scroll">
                              {item.prompt_detail}
                            </div>
                          }
                          className="dbo-prompt-saved-item-detail"
                          bubbleProps={{
                            overlayClassName: 'dbo-scroll-bubble',
                          }}
                        />
                        {item.update_time && (
                          <div className="dbo-prompt-saved-item-time">
                            {item.update_time}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Dropdown>
            <div
              className={`dbo-prompt-save-btn ${
                !prompt.trim() ? 'disabled' : ''
              }`}
              onClick={() => {
                if (!prompt.trim()) return;
                openSaveModal();
              }}
            >
              {/* <SvgIcon type="add-icon-type" className="dbo-prompt-save-icon" /> */}
              <i className="icon-prompt-save" />
              {formatMessage({ id: 'Save Current' })}
            </div>
          </div>
          <div
            className={`dbo-prompt-history-btn`}
            onClick={() => {
              togglePromptModal('history');
            }}
          >
            <CommonBubble content={formatMessage({ id: 'Historical prompt' })}>
              <SvgIcon type="updating" className="dbo-prompt-history-icon" />
            </CommonBubble>
          </div>
          {fullClick && (
            <div
              className={`dbo-prompt-history-btn`}
              onClick={() => {
                fullClick?.();
              }}
            >
              <SvgIcon type="scale" className="dbo-prompt-history-icon" />
            </div>
          )}
        </div>
      </div>
      {/* Prompt textarea */}
      <TextArea
        className="dbo-aigc-modal-textarea dbo-prompt-textarea"
        value={prompt}
        style={textareaHeight ? { height: textareaHeight, maxHeight:textareaHeight  } : {}}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onChange={(value) => {
          if (isBasic) {
            setErrorText(
              value.trim() ? '' : formatMessage({ id: 'Please enter prompt.' }),
            );
          }
          promptEdit.current = true;
          setActivePromptId('');
          setPrompt(value);
        }}
        placeholder={
          !isBasic
            ? formatMessage({
                id: 'Optional. If left blank, the pre-trained prompt provided by DataBrain will be used; alternatively, you can enter text to redefine the output format or further constrain the content of the summary.',
              })
            : undefined
        }
      />

      {/* Save / Edit Prompt Modal */}
      <SavePromptModal
        visible={saveModalVisible}
        mode={saveModalMode}
        editingPromptId={editingPromptId}
        initialPromptName={modalPromptName}
        initialPromptContent={modalPromptContent}
        onClose={closeSaveModal}
        onSavePrompt={onSavePrompt}
        onUpdatePrompt={onUpdatePrompt}
      />
    </div>
  );
};

export default PromptSection;
